import { Global } from "@emotion/react"
import { Suspense, useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { useParams } from "react-router-dom"

import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"
import { usePage } from "contexts/Page"
import { useSuspender } from "contexts/Suspender"
import { useUser } from "contexts/User"

import Gallery from "components/gallery/Gallery"
import ModelDownloads from "components/model/model-downloads/ModelDownloads"
import ModelFeatures from "components/model/model-features/ModelFeatures"
import ModelMajesty from "components/model/model-majesty/ModelMajesty"
import ModelRetailer from "components/model/model-retailer/ModelRetailer"
import ModelSpecifications from "components/model/model-specifications/ModelSpecifications"
import NextChapter from "components/next-chapter/NextChapter"
import Push from "components/push/Push"
import PushExtdFixed from "components/push/PushExtdFixed"
import YMAL from "components/ymal/Ymal"

import { colors } from "./colors"
import { ModelStateProvider } from "./context"

export default function BootstrapModel(props) {
  const console = useConsole()
  const locale = useLocale()
  const { countryCode } = useUser()
  const env = useEnv()
  const navigation = useNavigation()
  const { family, rmc } = useParams()
  const page = usePage()

  const { cloudinary: { secureDistribution } = {}, catalogYear } = env

  const [[err, model, offers, allowedPriceOnSSR], modelSSR] = useSuspender(
    ["model", locale.current.codes.www, family, rmc],
    () =>
      Promise.all([
        Promise.resolve(
          new URL(
            `/api/catalog/watches/${rmc}?language=${locale.current.codes.www}&terms=true`,
            process.browser ? env.services : process.env.SERVICE_CATALOG_URL
          )
        )
          .then(url => new Request(url))
          .then(request => fetch(request))
          .then(response => response.json())
          .then(data => {
            if (data.rmc === rmc && data.familyNode.code === family) return data
            throw { status: 404 }
          }),
        Promise.resolve(
          new URL(`/api/prices/structureddata/${rmc}?lang=${locale.current.codes.www}`, process.browser ? env.services : process.env.SERVICE_PRICES_URL)
        )
          .then(url => new Request(url))
          .then(request => fetch(request))
          .then(response => response.json())
          .then(data => data?.offers || null)
          .catch(err => {
            console.error(err)
            return null
          }),
        locale.current.codes.www !== "en-us" || countryCode !== "US" ? Promise.resolve(null) : Promise.resolve(new URL(`/api/prices/${rmc}?countryCode=${countryCode}&lang=${locale.current.codes.www}`, process.browser ? env.services : process.env.SERVICE_PRICES_URL ?? process.env.REVERSE_PROXY_URL))
          .then(url => new Request(url))
          .then(request => fetch(request))
          .then(response => response.json())
          .then(data => {
            if (!data.showPrices) {
              return null
            }
            return data
          })
          .catch(err => {
            console.error(err)
            return null
          })

      ])
        .then(([model, offers, allowedPriceOnSSR]) => {
          const title = page?.meta?.title
            .replaceAll("%rmc%", model?.rmc)
            .replaceAll("%material%", model?.label_material_title)
            .replaceAll("%name%", model?.name)
          const description = page?.meta?.description
            .replaceAll("%rmc%", model?.rmc)
            .replaceAll("%material%", model?.label_material_title)
            .replaceAll("%name%", model?.name)
          const ogtitle = (page?.meta?.title_og || page?.meta?.title || "")
            .replaceAll("%rmc%", model?.rmc)
            .replaceAll("%material%", model?.label_material_title)
            .replaceAll("%name%", model?.name)
          const ogdescription = (page?.meta?.description_og || page?.meta?.description || "")
            .replaceAll("%rmc%", model?.rmc)
            .replaceAll("%material%", model?.label_material_title)
            .replaceAll("%name%", model?.name)
          const ogimage = new URL(
            page?.meta?.image_og_cld || `image/upload/q_auto/f_auto/t_v7-main-configurator/c_limit,w_2440/v1/catalogue/${catalogYear}/upright-c/${model?.rmc}`,
            `https://${secureDistribution}`
          )
          const ogsearchimage = new URL(
            page?.meta?.searchimage_og_cld ||
            `image/upload/q_auto/f_auto/t_v7-main-configurator/c_limit,w_2440/v1/catalogue/${catalogYear}/upright-c/${model?.rmc}`,
            `https://${secureDistribution}`
          )
          const searchTitle = page?.meta?.search_title?.replaceAll("%name%", model?.name)
          return [
            null,
            Object.assign(model, { title, description, ogtitle, ogdescription, ogimage, ogsearchimage, searchTitle }),
            offers ? JSON.stringify(offers) : null,
            allowedPriceOnSSR
          ]
        })
        .catch(err => {
          return [err, null]
        }),
    true
  )

  if (err) {
    if (!process.browser) throw err
    navigation.push(navigation.localize("/404")) //TODO
  }

  useEffect(() => {
    props?.pageviewCallback?.()
  })

  console.verbose("BootstrapModel(%o)", { model })
  return (
    <>
      {modelSSR}
      <Helmet>
        <title>{model?.title}</title>
        <meta name='description' content={model?.description} />
        {model?.ogtitle && <meta name='og:title' content={model?.ogtitle} />}
        {model?.ogdescription && <meta name='og:description' content={model?.ogdescription} />}
        {model?.ogimage && <meta name='og:image' content={model?.ogimage} />}
        {model?.ogsearchimage && <meta name='og:searchimage' content={model?.ogsearchimage} />}
        {model?.searchTitle && <meta name='search_title' content={model?.searchTitle} />}
        {offers ? (
          <script type='application/ld+json'>{`
          {
            "@context":"http://schema.org",
            "@type":"Product",
            "name":"${model?.title}",
            "image":
              [
                "${model?.ogimage}"
              ],
            "description":"${model?.description}",
            "sku": "${rmc}",
            "mpn": "${rmc}",
            "brand": { "@type": "Thing", "name": "Rolex" },
            "offers": ${offers}
          }
        `}</script>
        ) : null}
      </Helmet>
      <Model model={model} allowedPriceOnSSR={allowedPriceOnSSR} {...props} />
    </>
  )
}

export function Model({ model, allowedPriceOnSSR, ymal, dictionary: localDictionary, brochure, user_guides, legal_agec, ...props }) {
  // const console = useConsole()
  const dictionary = useDictionary()

  const next = {
    label: dictionary.next(),
    jump_line: dictionary.modelpageJumpline(model?.familyNode?.name),
    link: { href: `/watches/${model?.familyNode.code}`, label: dictionary.continue() },
  }

  const ymalData = ymal?.[0]

  const fallbackObject = model?.suggestions_ymal_fallback

  const showGallery = model?.editorial_mapping?.has_beauty_shot_cards
  const galleryPictures =
    model && showGallery
      ? [
        {
          picture_cld: {
            alt: "",
            loading: "lazy",
            sizes: "100vw",
            sources: [
              {
                height: "2160",
                width: "3840",
                public_id: `rolexcom/model-page/gallery/${model.rmc}/${model.rmc}_v01`,
                policy: "",
              },
              {
                metadata: {
                  v7_type: ["portrait"],
                },
                height: "2600",
                width: "1560",
                public_id: `rolexcom/model-page/gallery/${model.rmc}/${model.rmc}_v01-portrait`,
                policy: "",
              },
            ],
          },
        },
        {
          picture_cld: {
            alt: "",
            loading: "lazy",
            sizes: "100vw",
            sources: [
              {
                height: "2160",
                width: "3840",
                public_id: `rolexcom/model-page/gallery/${model.rmc}/${model.rmc}_v02`,
                policy: "",
              },
              {
                metadata: {
                  v7_type: ["portrait"],
                },
                height: "2600",
                width: "1560",
                public_id: `rolexcom/model-page/gallery/${model.rmc}/${model.rmc}_v02-portrait`,
                policy: "",
              },
            ],
          },
        },
        {
          picture_cld: {
            alt: "",
            loading: "lazy",
            sizes: "100vw",
            sources: [
              {
                height: "2160",
                width: "3840",
                public_id: `rolexcom/model-page/gallery/${model.rmc}/${model.rmc}_v03`,
                policy: "",
              },
              {
                metadata: {
                  v7_type: ["portrait"],
                },
                height: "2600",
                width: "1560",
                public_id: `rolexcom/model-page/gallery/${model.rmc}/${model.rmc}_v03-portrait`,
                policy: "",
              },
            ],
          },
        },
      ]
      : null

  ymalData.rmcs = ["m268621-0004", "m116503-0004", "m226658-0001"]

  const rmcs = ymalData?.rmcs && ymalData?.rmcs?.length >= 3 ? ymalData?.rmcs : fallbackObject

  const {
    push_cta,
    push_url,
    push_title,
    push_text,
    push_subtitle,
    push_theme,
    push_image_portrait_cl,
    push_image_landscape_cl,
    push_type,
    push_border,
    push_parallax,
  } = model.editorial_mapping.editorial

  const isExternal = push_url?.search("http") === 0

  const pushProps = {
    heading: {
      kicker: "",
      subtitle: push_subtitle,
      title: push_title,
    },
    paragraph: push_text,
    link: {
      aria_label: push_cta,
      external: isExternal ? push_url : "",
      href: !isExternal ? push_url : "",
      icon: isExternal ? null : "chevron",
      label: push_cta,
      style: "inline",
    },
    image_cld: {
      alt: "",
      loading: "lazy",
      size: "100vw",
      sources: [
        {
          height: "1017",
          width: "2440",
          public_id: push_image_landscape_cl,
          policy: "",
        },
        {
          metadata: {
            v7_type: ["portrait"],
          },
          height: "1560",
          width: "2200",
          public_id: push_image_portrait_cl,
          policy: "",
        },
      ],
    },
    className: `${push_theme}-theme`,
    cta: [],
    meta: {
      index: 10,
    },
    marie_louise: push_border,
    parallax: push_parallax,
  }

  // console.verbose("Model(%o)", { model, brochure, user_guides, ymal, legal_agec })
  return (
    model && (
      <ModelStateProvider>
        <Global styles={colors} />
        <ModelMajesty localDictionary={localDictionary} {...model} ballerina={props.ballerina} allowedPriceOnSSR={allowedPriceOnSSR} />
        <ModelFeatures {...model.editorial_mapping} />
        {showGallery && <Gallery slides={galleryPictures} />}
        <ModelSpecifications dictionary={localDictionary} user_guides={user_guides} {...model} legal_agec={legal_agec} />
        <ModelDownloads brochure={brochure} user_guides={user_guides} model={model} />
        <ModelRetailer {...model.editorial_mapping.store} />

        {push_type === "normal" && <Push {...pushProps} />}
        {push_type === "fixed" && <PushExtdFixed {...pushProps} />}
        <Suspense>
          <YMAL {...ymalData} rmcs={rmcs} fallbackObject={fallbackObject} version='modelpage' />
        </Suspense>
        <NextChapter {...next} />
      </ModelStateProvider>
    )
  )
}
